<script setup lang="ts">
  import { ref, computed } from 'vue';
  import type { Section } from '@storefront/types';
  import type { ProductVariantResource } from '@storefront/resources';

  interface Props {
    variant: ProductVariantResource,
    sections: Section[],
    recommended: any[],
    reviews: any,
    reviewAvailable: boolean,
    reviewsSummary: any,
  }

  const props = defineProps<Props>();

  const parameters = ref({
    breakpoints: {
      1: {
        slidesPerView: 1.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      576: {
        slidesPerView: 2.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      768: {
        slidesPerView: 3.5,
        spaceBetween: 10,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      992: {
        slidesPerView: 4,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      1400: {
        slidesPerView: 5,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
  });

  const showSections = computed(() => {
    return props.sections.length > 0;
  });

  const showSpecification = computed(() => {
    return props.variant.attributes.length > 0;
  });

  const showSafety = computed(() => {
    return (
      props.variant.productManufacturer ||
      props.variant.productResponsibleEntity ||
      props.variant.productImporters.length > 0 ||
      props.variant.productSafetyNotes.length > 0
    );
  });

  const showRecommended = computed(() => {
    return props.recommended.length > 0;
  });

</script>
<template>
  <div class="container description-wrapper">
    <ShopProductViewDescriptionSection v-if="showSections" :title="$t('SHOP.PRODUCT.LABEL.DESCRIPTION')">
      <SectionRenderer :sections />
    </ShopProductViewDescriptionSection>
    <ShopProductViewDescriptionSection v-if="showSpecification" :title="$t('SHOP.PRODUCT.LABEL.SPECIFICATION')" class="description-wrapper__specification">
      <ShopProductViewDescriptionSpecification :variant />
    </ShopProductViewDescriptionSection>
    <ShopProductViewDescriptionSection v-if="showSafety" :title="$t('SHOP.PRODUCT.LABEL.SAFETY')">
      <ShopProductViewDescriptionSafety :variant />
    </ShopProductViewDescriptionSection>
    <ShopProductViewDescriptionSection :title="$t('SHOP.PRODUCT.LABEL.REVIEWS')">
      <ShopProductViewDescriptionReviews :variant :reviews :reviewAvailable :reviewsSummary />
    </ShopProductViewDescriptionSection>
    <ShopProductViewDescriptionSection v-if="showRecommended" :title="$t('SHOP.PRODUCT.LABEL.RECOMMENDED')">
      <div class="products-wrapper">
        <SfProductsCarousel :products="recommended" :parameters />
      </div>
    </ShopProductViewDescriptionSection>
  </div>
</template>

<style lang="scss">
  :root {
    --shop-product-view-description-padding: 1.5rem;
    --shop-product-view-description-gap: 3rem;
    --shop-product-view-description-background: var(--background-card);
    --shop-product-view-description-border-radius: var(--border-radius-card);
    --shop-product-view-description-border: var(--border-card);

    @media (max-width: 992px) {
      --shop-product-view-description-padding: 1rem 0.625rem;
      --shop-product-view-description-gap: 1.5rem;
    }
  }
</style>
<style scoped lang="scss">
  .description-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: var(--shop-product-view-description-gap);
    padding: var(--shop-product-view-description-padding);
    background: var(--shop-product-view-description-background);
    border-radius: var(--shop-product-view-description-border-radius);
    border: var(--shop-product-view-description-border);

    @media (max-width: 992px) {
      margin: 1rem auto;
    }
  }
</style>
